import { all, both, lensProp, over, pick, pipe, values } from 'ramda';

import { getTravellersCount } from '@shared/utils';

import { BOOKABLE_PERIOD, MAX_STAY, MAX_TRAVELLERS_COUNT } from '@/constants';
import { addDays, startOfDay } from '@/lib/date';
import getFeatureFlag from '@/utils/getFeatureFlag';

export const validateOriginCode = ({ originCodes }, { originCode }) =>
  originCodes.includes(originCode);

export const validateDateRange = (_, { departureDate, returnDate }) => {
  if (!departureDate && returnDate) {
    return false;
  }

  const d = startOfDay(new Date());
  const dd = new Date(departureDate);
  const rd = new Date(returnDate);

  const isValidDepartureDate = dd >= addDays(d, BOOKABLE_PERIOD.min);
  const isValidReturnDate = rd <= addDays(d, BOOKABLE_PERIOD.max);
  const isValidRange = rd > dd;
  const isBeforeMaxStayDate = rd < addDays(dd, MAX_STAY);

  return [
    isValidDepartureDate,
    isValidReturnDate,
    isValidRange,
    isBeforeMaxStayDate,
  ].every((v) => !!v);
};

const isValidNumber = (numberLike) =>
  pipe(Number, both(Number.isFinite, Number.isInteger))(numberLike);

const numericValidationProps = ['adults', 'children', 'infants'];
const numericValidationLenses = numericValidationProps.map((prop) =>
  over(lensProp(prop), isValidNumber),
);

export const validateTravellerCounts = (query) =>
  pipe(
    ...[
      pick(numericValidationProps),
      ...numericValidationLenses,
      values,
      all((v) => !!v),
    ],
  )(query);

export const validateNumberOfChildren = (data) =>
  !data ||
  !getFeatureFlag('KFF_CHILDREN_LIMIT') ||
  data.children <= data.adults;
export const validateNumberOfInfants = (data) =>
  !data || data.infants <= data.adults;

export const validateTravellers = (_, data) =>
  validateTravellerCounts(data) &&
  getTravellersCount(data) <= MAX_TRAVELLERS_COUNT &&
  validateNumberOfChildren(data) &&
  validateNumberOfInfants(data);

const validateSearchQuery = (config, searchQuery) =>
  [validateOriginCode, validateDateRange, validateTravellers]
    .map((fn) => fn(config, searchQuery))
    .every((v) => !!v);

export default validateSearchQuery;
